import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import type { TradeProposalsParams } from 'src/apis/monolith/types';
import { useAuctionTiles } from 'src/apis/monolith/use-auction-tiles';
import { useFavouriteNetworkPointsMonolith } from 'src/apis/monolith/use-favourite-network-points';
import { useTradeProposals } from 'src/apis/monolith/use-trade-proposals';
import {
  type MyUserDetailsRequest,
  useMyUserDetails,
} from 'src/apis/organisation-service/use-my-user-details';
import {
  type OptionalOrganisationDetailsRequest,
  useOptionalOrganisationDetails,
} from 'src/apis/organisation-service/use-organisation-details';
import { useOrganisationService } from 'src/apis/organisation-service/use-organisation-service';
import { useOrganisationServicePublic } from 'src/apis/organisation-service/use-organisation-service-public';
import {
  type PublicOperatorsRequest,
  usePublicOperators,
} from 'src/apis/organisation-service/use-public-operators';
import { useAuctionTiles as useAuctionTilesTAS } from 'src/apis/transport-auction/use-auction-tiles';
import { useOptionalTransportAuctionServiceRoot } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useOptionalTtpcModulationService } from 'src/apis/ttpc-modulation-service/use-ttpc-modulation-service';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useOptionalDashboardNews } from 'src/hooks/use-dashboard-news';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import {
  useDashboardPageParams,
  type DashboardPageParams,
} from 'src/pages/dashboard/use-page-params';
import { useBidCollectorRoot } from 'src/pages/snam-bid-collector/use-bid-collector';

const Page = lazy(() => import('./page'));

export const Dashboard: FC = () => {
  const organisationServicePublic = useOrganisationServicePublic();
  const operators = usePublicOperators({
    randomOrder: true,
    organisationServicePublic,
  });
  const organisationService = useOrganisationService();
  const myUserDetails = useMyUserDetails({ organisationService });
  const myOrganisationDetails = useOptionalOrganisationDetails({
    organisationService,
  });
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const dashboardNews = useOptionalDashboardNews();
  const pageParams = useDashboardPageParams();

  if (
    !operators.response ||
    !myUserDetails.response ||
    !myOrganisationDetails?.response ||
    dashboardNews?.loaded === false
  )
    return <PageSpinner />;

  if (monolithUser?.isShipper) {
    return (
      <LoadShipperData
        operators={operators}
        myUserDetails={myUserDetails}
        myOrganisationDetails={myOrganisationDetails}
        pageParams={pageParams}
      />
    );
  }

  return (
    <Page
      operators={operators}
      myUserDetails={myUserDetails}
      myOrganisationDetails={myOrganisationDetails}
      dashboardNews={dashboardNews}
      pageParams={pageParams}
    />
  );
};

type LoadShipperDataProps = {
  operators: Successful<PublicOperatorsRequest>;
  myUserDetails: Successful<MyUserDetailsRequest>;
  myOrganisationDetails: Successful<OptionalOrganisationDetailsRequest>;
  pageParams: DashboardPageParams;
};

const LoadShipperData: FC<LoadShipperDataProps> = ({
  operators,
  myUserDetails,
  myOrganisationDetails,
  pageParams,
}) => {
  const auctionTiles = useAuctionTiles();

  const transportAuctionServiceRoot = useOptionalTransportAuctionServiceRoot();
  const auctionTilesTAS = useAuctionTilesTAS({
    root: transportAuctionServiceRoot,
  });

  const snamBidCollector = useBidCollectorRoot();

  const ttpcModulationService = useOptionalTtpcModulationService();

  const favouriteNetworkPointsParams = useMemo(
    () => ({
      pageSize: pageParams.favouriteNetworkPoints.value.pageSize,
      start: pageParams.favouriteNetworkPoints.value.start,
    }),
    [
      pageParams.favouriteNetworkPoints.value.pageSize,
      pageParams.favouriteNetworkPoints.value.start,
    ]
  );
  const favouriteNetworkPoints = useFavouriteNetworkPointsMonolith(
    favouriteNetworkPointsParams
  );

  const ownProposalsParams = useMemo(
    () =>
      ({
        mode: 'my',
        sortAscending:
          pageParams.ownTradeProposals.value.sortDirection === 'asc',
        sortColumn: pageParams.ownTradeProposals.value.sortBy,
        pageSize: pageParams.ownTradeProposals.value.pageSize,
        start: pageParams.ownTradeProposals.value.start,
        networkPointName: pageParams.ownTradeProposals.value.networkPoint.map(
          (networkPoint) => networkPoint.value
        ),
        tsoName: pageParams.ownTradeProposals.value.tso.map(
          (tso) => tso.shortName
        ),
        'amount-min': pageParams.ownTradeProposals.value.amountMin,
        'amount-max': pageParams.ownTradeProposals.value.amountMax,
        runtimeFrom: pageParams.ownTradeProposals.value.runtimeFrom,
        runtimeTo: pageParams.ownTradeProposals.value.runtimeTo,
        procedure: pageParams.ownTradeProposals.value.procedure,
        capacityCategory: pageParams.ownTradeProposals.value.capacityCategory,
        direction: pageParams.ownTradeProposals.value.direction,
      }) satisfies TradeProposalsParams,
    [pageParams.ownTradeProposals.value]
  );
  const ownProposals = useTradeProposals(ownProposalsParams);

  const params = useMemo(
    () =>
      ({
        mode: 'atMyNetworkPoints',
        sortAscending:
          pageParams.proposalsFavouriteNetworkPoint.value.sortDirection ===
          'asc',
        sortColumn: pageParams.proposalsFavouriteNetworkPoint.value.sortBy,
        pageSize: pageParams.proposalsFavouriteNetworkPoint.value.pageSize,
        start: pageParams.proposalsFavouriteNetworkPoint.value.start,
        networkPointName:
          pageParams.proposalsFavouriteNetworkPoint.value.networkPoint.map(
            (networkPoint) => networkPoint.value
          ),
        tsoName: pageParams.proposalsFavouriteNetworkPoint.value.tso.map(
          (tso) => tso.shortName
        ),
        'amount-min': pageParams.proposalsFavouriteNetworkPoint.value.amountMin,
        'amount-max': pageParams.proposalsFavouriteNetworkPoint.value.amountMax,
        runtimeFrom:
          pageParams.proposalsFavouriteNetworkPoint.value.runtimeFrom,
        runtimeTo: pageParams.proposalsFavouriteNetworkPoint.value.runtimeTo,
        procedure: pageParams.proposalsFavouriteNetworkPoint.value.procedure,
        capacityCategory:
          pageParams.proposalsFavouriteNetworkPoint.value.capacityCategory,
        direction: pageParams.proposalsFavouriteNetworkPoint.value.direction,
      }) satisfies TradeProposalsParams,
    [pageParams.proposalsFavouriteNetworkPoint.value]
  );
  const proposalsFavouriteNetworkPoint = useTradeProposals(params);

  if (
    transportAuctionServiceRoot?.loaded === false ||
    auctionTilesTAS?.response === null ||
    !auctionTiles.response ||
    !snamBidCollector.response ||
    (PRISMA_CONFIG.ttpcModulationService && !ttpcModulationService.loaded) ||
    !favouriteNetworkPoints.response ||
    ownProposals.response === null ||
    proposalsFavouriteNetworkPoint.response === null
  )
    return <PageSpinner />;

  return (
    <Page
      operators={operators}
      myUserDetails={myUserDetails}
      myOrganisationDetails={myOrganisationDetails}
      auctionTiles={auctionTiles}
      auctionTilesTAS={auctionTilesTAS}
      snamBidCollector={snamBidCollector}
      ttpcModulationService={ttpcModulationService}
      favouriteNetworkPoints={favouriteNetworkPoints}
      ownProposals={ownProposals}
      proposalsFavouriteNetworkPoint={proposalsFavouriteNetworkPoint}
      pageParams={pageParams}
    />
  );
};
